/**
 * Error Tracking Utility
 * Comprehensive error tracking for web application
 * Uses Firebase Analytics for error reporting
 */

import { trackError } from './analytics';

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  FATAL: 'fatal',
};

/**
 * Log error to Firebase Analytics
 * @param {Error} error - Error object
 * @param {string} context - Where error occurred
 * @param {string} severity - Error severity level
 * @param {object} additionalData - Additional context
 */
export const logError = (error, context = 'unknown', severity = ErrorSeverity.ERROR, additionalData = {}) => {
  console.error(`[${severity.toUpperCase()}] Error in ${context}:`, error, additionalData);
  
  // Track in Firebase Analytics
  trackError(
    error.message || String(error),
    context,
    severity,
    {
      stack: error.stack?.substring(0, 500), // Limit stack trace length
      ...additionalData,
    }
  );
};

/**
 * Log caught exception
 * @param {Error} error - Error object
 * @param {string} context - Context where caught
 * @param {object} metadata - Additional metadata
 */
export const logCaughtException = (error, context, metadata = {}) => {
  logError(error, context, ErrorSeverity.ERROR, {
    type: 'caught_exception',
    ...metadata,
  });
};

/**
 * Log API error
 * @param {Error} error - Error object
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {object} requestData - Request data
 */
export const logAPIError = (error, endpoint, method = 'GET', requestData = {}) => {
  logError(error, 'api_call', ErrorSeverity.ERROR, {
    endpoint,
    method,
    status_code: error.response?.status || 'unknown',
    request_data: JSON.stringify(requestData).substring(0, 200),
  });
};

/**
 * Log authentication error
 * @param {Error} error - Error object
 * @param {string} authMethod - Authentication method
 */
export const logAuthError = (error, authMethod = 'unknown') => {
  logError(error, 'authentication', ErrorSeverity.ERROR, {
    auth_method: authMethod,
    error_code: error.code || 'unknown',
  });
};

/**
 * Log navigation error
 * @param {Error} error - Error object
 * @param {string} route - Target route
 */
export const logNavigationError = (error, route) => {
  logError(error, 'navigation', ErrorSeverity.WARNING, {
    target_route: route,
  });
};

/**
 * Log component error
 * @param {Error} error - Error object
 * @param {string} componentName - React component name
 * @param {object} componentProps - Component props
 */
export const logComponentError = (error, componentName, componentProps = {}) => {
  logError(error, 'component_render', ErrorSeverity.ERROR, {
    component: componentName,
    props: JSON.stringify(componentProps).substring(0, 200),
  });
};

/**
 * Log async operation error
 * @param {Error} error - Error object
 * @param {string} operationName - Name of operation
 */
export const logAsyncError = (error, operationName) => {
  logError(error, 'async_operation', ErrorSeverity.ERROR, {
    operation: operationName,
  });
};

/**
 * Log storage error
 * @param {Error} error - Error object
 * @param {string} storageType - Type of storage (localStorage, sessionStorage)
 * @param {string} key - Storage key
 */
export const logStorageError = (error, storageType, key) => {
  logError(error, 'storage', ErrorSeverity.WARNING, {
    storage_type: storageType,
    key: key,
  });
};

/**
 * Log network error
 * @param {Error} error - Error object
 * @param {string} url - Request URL
 */
export const logNetworkError = (error, url) => {
  logError(error, 'network', ErrorSeverity.ERROR, {
    url: url,
    network_status: navigator.onLine ? 'online' : 'offline',
  });
};

/**
 * Log validation error
 * @param {string} message - Validation error message
 * @param {string} field - Field that failed validation
 * @param {any} value - Invalid value
 */
export const logValidationError = (message, field, value) => {
  logError(new Error(message), 'validation', ErrorSeverity.WARNING, {
    field: field,
    invalid_value: String(value).substring(0, 100),
  });
};

/**
 * Initialize global error handlers
 */
export const initializeErrorTracking = () => {
  // Global error handler
  window.addEventListener('error', (event) => {
    logError(
      event.error || new Error(event.message),
      'global_error',
      ErrorSeverity.ERROR,
      {
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      }
    );
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      'unhandled_promise_rejection',
      ErrorSeverity.ERROR,
      {
        promise: String(event.promise),
      }
    );
  });

  // Console error override (optional - capture console.error calls)
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // Call original console.error
    originalConsoleError.apply(console, args);
    
    // Track significant errors (not warnings or info)
    if (args[0] instanceof Error) {
      logError(args[0], 'console_error', ErrorSeverity.WARNING);
    }
  };

  console.log('🛡️ Error tracking initialized');
};

/**
 * Create error boundary HOC
 * @param {React.Component} Component - Component to wrap
 * @param {string} componentName - Component name for tracking
 */
export const withErrorTracking = (Component, componentName) => {
  return class ErrorBoundary extends React.Component {
    componentDidCatch(error, errorInfo) {
      logComponentError(error, componentName, {
        componentStack: errorInfo.componentStack?.substring(0, 300),
      });
    }

    render() {
      return <Component {...this.props} />;
    }
  };
};

/**
 * Breadcrumb tracking for debugging
 */
class BreadcrumbTracker {
  constructor(maxBreadcrumbs = 50) {
    this.breadcrumbs = [];
    this.maxBreadcrumbs = maxBreadcrumbs;
  }

  add(message, category = 'default', data = {}) {
    this.breadcrumbs.push({
      timestamp: new Date().toISOString(),
      message,
      category,
      data,
    });

    // Keep only last N breadcrumbs
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs.shift();
    }
  }

  get() {
    return this.breadcrumbs;
  }

  clear() {
    this.breadcrumbs = [];
  }
}

export const breadcrumbs = new BreadcrumbTracker();

/**
 * Add breadcrumb for user action
 * @param {string} action - Action description
 * @param {object} data - Additional data
 */
export const addBreadcrumb = (action, data = {}) => {
  breadcrumbs.add(action, 'user_action', data);
};

/**
 * Add navigation breadcrumb
 * @param {string} from - Previous route
 * @param {string} to - New route
 */
export const addNavigationBreadcrumb = (from, to) => {
  breadcrumbs.add(`Navigation: ${from} → ${to}`, 'navigation', { from, to });
};

/**
 * Add API breadcrumb
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {string} status - Response status
 */
export const addAPIBreadcrumb = (endpoint, method, status) => {
  breadcrumbs.add(`API ${method} ${endpoint}`, 'api', { endpoint, method, status });
};

/**
 * Get error context for reporting
 * @returns {object} - Error context
 */
export const getErrorContext = () => {
  return {
    userAgent: navigator.userAgent,
    url: window.location.href,
    online: navigator.onLine,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    breadcrumbs: breadcrumbs.get().slice(-10), // Last 10 breadcrumbs
    timestamp: new Date().toISOString(),
  };
};

/**
 * Test error tracking (for development)
 */
export const testErrorTracking = () => {
  console.log('Testing error tracking...');
  
  // Test 1: Simple error
  logError(new Error('Test error'), 'test', ErrorSeverity.INFO);
  
  // Test 2: API error simulation
  logAPIError(new Error('API test error'), '/api/test', 'POST');
  
  // Test 3: Breadcrumb
  addBreadcrumb('Test action', { test: true });
  
  console.log('Error tracking test complete. Check Firebase Console.');
};
